<script setup lang="ts">
import {
  Author,
  FeaturedNewsFieldsFragment,
  MediaImageFragment,
  NewsFieldsFragment,
  ParagraphAnchors,
  ParagraphSpecificTagsNews,
  TeaserSliderFieldsFragment,
} from '~/api/graphql/generated'
import { Link } from '~/types'
import { AuthorProps } from '../sidebar/components/AuthorSidebar.vue'
import { format, parse } from 'date-fns'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
  entity: NewsFieldsFragment
  breadcrumbs?: Link[]
}>()

const introText = computed(() => props.entity.fieldLead?.processed || '')

const date = computed(() =>
  props.entity?.fieldPublicationDate?.date
    ? format(parse(props.entity.fieldPublicationDate.date, "yyyy-MM-dd HH:mm:ss 'UTC", new Date()), 'dd.MM.yyyy')
    : '',
)

const relatedFields = computed(() => {
  switch (props.entity.fieldRelated?.entity?.__typename) {
    case 'ParagraphFeaturedNewsPosts':
      return { entity: props.entity.fieldRelated.entity as FeaturedNewsFieldsFragment, type: 'FeaturedNews' }
    case 'ParagraphTeaserSlider':
      return { entity: props.entity.fieldRelated.entity as TeaserSliderFieldsFragment, type: 'TeaserSlider' }
    case 'ParagraphSpecificTagsNews':
      return { entity: props.entity.fieldRelated.entity as ParagraphSpecificTagsNews, type: 'SpecificTags' }
    default:
      return null
  }
})

const sidebarAuthor = computed(() => {
  const user = props.entity.fieldAuthor?.entity as Author
  if (!user) return

  const userImage = user.fieldPicture?.entity as MediaImageFragment
  return {
    image: userImage || {
      fieldMediaImage: { url: '/tocco-user-placeholder.png', height: 135, width: 135 },
    },
    name: user.title,
    alt: userImage.fieldMediaImage?.alt,
    job: user.fieldJobTitle,
  }
})

const sidebarTags = computed(() =>
  props.entity.fieldVisibleTags?.filter(Boolean).map((tag) => {
    return {
      id: tag.entity?.tid?.toString() ?? '',
      name: tag.entity?.name ?? '',
    }
  }),
)
</script>

<template>
  <SectionHeroNews :entity="entity" :breadcrumbs="breadcrumbs" />
  <SectionIntrotext v-if="introText" :text="introText" />
  <section v-if="date" v-animate="{ animation: 'vertical-reveal' }" class="section section-date container grid">
    <span class="text-small t-10 t-o-1 tl-o-0 ds-o-1 ds-8">{{ date }}</span>
  </section>
  <SectionAnchors v-if="entity.fieldAnchors?.entity" :entity="entity.fieldAnchors.entity as ParagraphAnchors" />
  <SectionSidebar>
    <AuthorSidebar v-if="sidebarAuthor" :title="t('author.title')" :author="sidebarAuthor as AuthorProps" />

    <TextLinkSidebar
      v-if="entity.fieldEmail"
      :to="`mailto:${entity.fieldEmail}`"
      :title="t('news.detail.messageCTALabel')"
    >
      {{ t('news.detail.messageCTA') }}
    </TextLinkSidebar>

    <TagsSidebar v-if="sidebarTags" :title="t('tags.title')" :tags="sidebarTags" />
  </SectionSidebar>
  <Sections :entity="entity" />
  <SectionComments
    :post-id="entity.entityId!"
    :status="entity.fieldComments?.status!"
    :count="entity.fieldComments?.commentCount!"
  />
  <SectionFeaturedNewsPosts
    v-if="relatedFields && relatedFields.type === 'FeaturedNews'"
    :entity="relatedFields.entity as FeaturedNewsFieldsFragment"
  />
  <SectionTeaserSlider
    v-if="relatedFields && relatedFields.type === 'TeaserSlider'"
    :entity="relatedFields.entity as TeaserSliderFieldsFragment"
  />
  <SectionSpecificTagsNews
    v-if="relatedFields && relatedFields.type === 'SpecificTags'"
    :entity="relatedFields.entity as ParagraphSpecificTagsNews"
  />
</template>
<style scoped lang="scss">
.section-hero-news {
  @include fluid(margin-bottom, 30px, 64px);
}
.section-introtext {
  margin-bottom: 0;
}
.section-date {
  --margin: 0;
  margin-top: 8px;
  color: var(--c-text-medium);
}
</style>
